import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import SharedButton from '../../../../shared/buttons/shared-button';
import SortableList from '../../../../shared/sortable-list';
import { useStore } from '../../../state/zustand-store';
import PasteOptionsDialog from './paste-options-dialog';
import OptionActionsMenu from './option-actions-menu';
import LockIndicator from '../edit-label/lock-indicator';
import { LockIndex, NO_LOCKS } from '../label-locks';
import {
  newOption,
  nextOptionName,
  optionsThatFit,
} from './label-draft-mapper';
import {
  BADGE_COLORS,
  badgeTextColor,
  CreateLabelForm,
  LABEL_LIMITS,
  OPTION_ORDER_HELP_URL,
} from './label-schema';
import type { LabelFieldType } from '../../../../api/drive-labels-api';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FormatColorResetOutlined from '@mui/icons-material/FormatColorResetOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface FieldOptionsEditorProps {
  control: Control<CreateLabelForm>;
  errors: FieldErrors<CreateLabelForm>;
  fieldIndex: number;
  fieldType: LabelFieldType;
  onDragStateChange: (isDragging: boolean) => void;
  /** Edit Label: the field's Google id, to look up its locks. */
  fieldId?: string;
  /** Edit Label: the label's Google rule locks. */
  locks?: LockIndex;
  /** Edit Label: opens the Rules step from a lock's popover. */
  onViewRules?: () => void;
}

/** The Admin console's note when a rule stops the options from being reordered. */
export const OPTION_REORDER_LOCKED_NOTE =
  'Re-ordering of options is restricted for this field as some options are associated with a rule';

/** The marker on a disabled option, saying how it shows when the label is applied. */
function DisabledOptionMarker({ shown }: { shown: boolean }) {
  const text = shown
    ? "Option is shown but can't be selected"
    : 'Option is hidden';
  return (
    <Tooltip title={text}>
      <InfoOutlined
        aria-label={text}
        sx={{ fontSize: 20, color: 'text.secondary', alignSelf: 'center' }}
      />
    </Tooltip>
  );
}

function BadgeColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  const swatch = (color: string) => (
    <Box
      key={color}
      component="button"
      type="button"
      aria-label={`Badge color ${color}`}
      aria-pressed={value.toLowerCase() === color}
      onClick={() => onChange(color)}
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: color,
        cursor: 'pointer',
        border: '2px solid',
        borderColor:
          value.toLowerCase() === color ? 'primary.main' : 'transparent',
        outlineOffset: 2,
        p: 0,
      }}
    />
  );
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Tooltip title="No fill">
        <IconButton
          size="small"
          aria-label="No badge color"
          aria-pressed={value === ''}
          onClick={() => onChange('')}
          sx={{
            border: '2px solid',
            borderColor: value === '' ? 'primary.main' : 'transparent',
          }}
        >
          <FormatColorResetOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {BADGE_COLORS.light.map(swatch)}
      {BADGE_COLORS.dark.map(swatch)}
    </Stack>
  );
}

export default function FieldOptionsEditor({
  control,
  errors,
  fieldIndex,
  fieldType,
  onDragStateChange,
  fieldId = '',
  locks = NO_LOCKS,
  onViewRules,
}: FieldOptionsEditorProps) {
  const {
    fields: options,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: `fields.${fieldIndex}.options`,
    keyName: 'rhfKey',
  });
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [pasteOpen, setPasteOpen] = useState(false);
  const { setSnackbar } = useStore();

  const isBadge = fieldType === 'BADGE_LIST';
  const showOrderHint =
    fieldType === 'BADGE_LIST' || fieldType === 'SINGLE_SELECT';
  const atLimit = options.length >= LABEL_LIMITS.maxOptions;
  const optionErrors = errors.fields?.[fieldIndex]?.options;
  // A Google rule lock on the field blocks new options and reordering. A locked option can't move
  // either, and moving the others past it would move it, so the whole list stays in place.
  // Live values: the menu replaces a whole option, which the field array's items don't reflect.
  const watchedOptions = useWatch({
    control,
    name: `fields.${fieldIndex}.options`,
  });
  const fieldLocked = locks.isFieldLocked(fieldId);
  const reorderLocked =
    fieldLocked ||
    options.some((option) => locks.isOptionLocked(fieldId, option.id));

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleAdd = () => {
    const option = newOption(
      nextOptionName(options, isBadge ? 'Badge option' : 'Option')
    );
    append(option);
    if (isBadge) {
      setExpandedKeys((prev) => new Set(prev).add(option.key));
    }
  };

  const handlePaste = (names: string[]) => {
    const added = optionsThatFit(
      names,
      options.length,
      LABEL_LIMITS.maxOptions
    );
    append(added.map((name) => newOption(name)));
    if (added.length < names.length) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Only ${added.length} options were added. A field can have at most ${LABEL_LIMITS.maxOptions} options.`,
      });
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 500 }}>Options</Typography>
          {reorderLocked ? (
            // Like the design: the note fills the column, with no help link.
            <Typography variant="caption" color="text.secondary" component="p">
              {OPTION_REORDER_LOCKED_NOTE}
            </Typography>
          ) : (
            showOrderHint && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Order matters for labels used in policy
                </Typography>
                <Link
                  href={OPTION_ORDER_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Learn why option order matters"
                  sx={{ display: 'flex' }}
                >
                  <InfoOutlined sx={{ fontSize: 16 }} color="info" />
                </Link>
              </Stack>
            )
          )}
        </Box>
        <SharedButton
          variant="secondary"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={atLimit || fieldLocked}
          sx={{ flexShrink: 0 }}
        >
          Add
        </SharedButton>
      </Stack>

      {typeof optionErrors?.message === 'string' && (
        <Typography variant="caption" color="error">
          {optionErrors.message}
        </Typography>
      )}

      <SortableList
        items={options}
        getId={(option) => option.key}
        onMove={move}
        onDragStateChange={onDragStateChange}
        gap={0}
        renderItem={(option, optionIndex, handle, isDragging) => {
          const expanded =
            isBadge && !isDragging && expandedKeys.has(option.key);
          const nameError = optionErrors?.[optionIndex]?.name?.message;
          return (
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems={isBadge ? 'center' : 'flex-start'}
                sx={{
                  py: 1,
                  px: 0.5,
                  bgcolor: expanded ? 'action.hover' : undefined,
                  borderRadius: 1,
                }}
              >
                {reorderLocked ? (
                  // Keeps the names lined up with the other fields' options.
                  <Box sx={{ width: 34, flexShrink: 0 }} />
                ) : (
                  <IconButton
                    ref={handle.setActivatorNodeRef}
                    {...handle.attributes}
                    {...handle.listeners}
                    size="small"
                    aria-label="Drag to reorder option"
                    sx={{
                      cursor: isDragging ? 'grabbing' : 'grab',
                      mt: isBadge ? 0 : 0.5,
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </IconButton>
                )}

                {isBadge ? (
                  <Controller
                    control={control}
                    name={`fields.${fieldIndex}.options.${optionIndex}`}
                    render={({ field }) => (
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Chip
                          size="small"
                          label={field.value.name || 'Untitled'}
                          color={nameError ? 'error' : undefined}
                          sx={{
                            // "No fill" in the design is just the text: no pill background.
                            bgcolor: nameError
                              ? undefined
                              : field.value.badgeColor || 'transparent',
                            color: nameError
                              ? undefined
                              : badgeTextColor(field.value.badgeColor),
                            maxWidth: '100%',
                          }}
                        />
                      </Box>
                    )}
                  />
                ) : (
                  <Controller
                    control={control}
                    name={`fields.${fieldIndex}.options.${optionIndex}.name`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id={`option-name-${option.key}`}
                        label="Option name"
                        required
                        fullWidth
                        slotProps={{
                          htmlInput: {
                            maxLength: LABEL_LIMITS.maxOptionNameLength,
                          },
                        }}
                        error={!!nameError}
                        helperText={nameError}
                      />
                    )}
                  />
                )}

                {/* Edit Label: a disabled option says how it shows, before the row's controls. */}
                {watchedOptions?.[optionIndex]?.disabled && (
                  <DisabledOptionMarker
                    shown={watchedOptions[optionIndex]?.showInApply !== false}
                  />
                )}
                {isBadge && (
                  <IconButton
                    size="small"
                    aria-label={expanded ? 'Collapse option' : 'Expand option'}
                    onClick={() => toggleExpanded(option.key)}
                  >
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
                {/* One fixed slot for the lock, the menu or the trash, so every row lines up. */}
                <Box
                  sx={{
                    width: 36,
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    mt: isBadge ? 0 : 0.5,
                  }}
                >
                  {/* Edit Label: a lock blocks disabling and deleting; renaming stays allowed. */}
                  {locks.isOptionLocked(fieldId, option.id) ? (
                    <LockIndicator
                      name={option.name || 'Option'}
                      level="option"
                      onViewRules={onViewRules}
                    />
                  ) : option.published ? (
                    // Edit Label: a live option is disabled first, then deleted, from its menu.
                    !fieldLocked && (
                      <Controller
                        control={control}
                        name={`fields.${fieldIndex}.options.${optionIndex}`}
                        render={({ field }) => (
                          <OptionActionsMenu
                            name={field.value.name || 'Option'}
                            disabled={!!field.value.disabled}
                            savedDisabled={!!field.value.savedDisabled}
                            isLastOption={options.length <= 1}
                            onDisable={(showInApply) =>
                              field.onChange({
                                ...field.value,
                                disabled: true,
                                showInApply,
                              })
                            }
                            onEnable={() =>
                              field.onChange({
                                ...field.value,
                                disabled: false,
                              })
                            }
                            onDelete={() => remove(optionIndex)}
                          />
                        )}
                      />
                    )
                  ) : (
                    <Tooltip
                      title={
                        options.length <= 1
                          ? 'A field needs at least one option'
                          : 'Delete option'
                      }
                    >
                      <Box component="span">
                        <IconButton
                          size="small"
                          aria-label="Delete option"
                          disabled={options.length <= 1}
                          onClick={() => remove(optionIndex)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Stack>

              {expanded && (
                <Stack
                  spacing={1.5}
                  sx={{ px: 6, pb: 2, pt: 1, bgcolor: 'action.hover' }}
                >
                  <Stack direction="row" spacing={2}>
                    <Controller
                      control={control}
                      name={`fields.${fieldIndex}.options.${optionIndex}.name`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`option-name-${option.key}`}
                          label="Option name"
                          required
                          fullWidth
                          slotProps={{
                            htmlInput: {
                              maxLength: LABEL_LIMITS.maxOptionNameLength,
                            },
                          }}
                          error={!!nameError}
                          helperText={nameError}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`fields.${fieldIndex}.options.${optionIndex}.description`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`option-description-${option.key}`}
                          label="Description (optional)"
                          fullWidth
                          slotProps={{
                            htmlInput: {
                              maxLength:
                                LABEL_LIMITS.maxOptionDescriptionLength,
                            },
                          }}
                          error={!!optionErrors?.[optionIndex]?.description}
                          helperText={
                            optionErrors?.[optionIndex]?.description?.message
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Controller
                    control={control}
                    name={`fields.${fieldIndex}.options.${optionIndex}.badgeColor`}
                    render={({ field }) => (
                      <BadgeColorPicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Stack>
              )}
              <Divider />
            </Box>
          );
        }}
      />

      <Box>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setPasteOpen(true)}
          disabled={atLimit || fieldLocked}
        >
          Paste multiple
        </Link>
      </Box>

      <PasteOptionsDialog
        open={pasteOpen}
        handleClose={() => setPasteOpen(false)}
        onAdd={handlePaste}
      />
    </Stack>
  );
}
